import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { HeartPulse, ArrowLeft, Eye, EyeOff, KeyRound, MailCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1 = email, 2 = code+new pw, 3 = done
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const requestCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim().toLowerCase() });
      toast.info("Si ce compte existe, un email avec le code vient de vous être envoyé. Vérifiez votre boîte de réception.");
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la demande");
    } finally {
      setLoading(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (newPw.length < 8) { toast.error("Mot de passe minimum 8 caractères"); return; }
    if (!/[A-Z]/.test(newPw) || !/\d/.test(newPw)) {
      toast.error("Le mot de passe doit contenir au moins une majuscule et un chiffre"); return;
    }
    if (newPw !== confirmPw) { toast.error("Les deux mots de passe ne correspondent pas"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        email: email.trim().toLowerCase(),
        code: code.trim().toUpperCase(),
        new_password: newPw,
      });
      setStep(3);
      toast.success("Mot de passe réinitialisé");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de réinitialisation");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] grid lg:grid-cols-2">
      <div className="hidden lg:block relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1665231795856-769fb08a90bc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjbGluaWMlMjBpbnRlcmlvciUyMHdhcm18ZW58MHx8fHwxNzc4MzIyMzAyfDA&ixlib=rb-4.1.0&q=85"
          alt="Clinic" className="w-full h-full object-cover"/>
        <div className="absolute inset-0 bg-gradient-to-tr from-[#1E4631]/85 via-[#1E4631]/40 to-transparent"/>
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-xl bg-white grid place-items-center">
              <HeartPulse className="w-5 h-5 text-[#1E4631]"/>
            </div>
            <span className="font-[Outfit] font-bold text-2xl">KWIKILA Care</span>
          </div>
          <h2 className="font-[Outfit] text-3xl font-bold leading-tight max-w-md">
            Réinitialisez votre mot de passe en toute sécurité.
          </h2>
        </div>
      </div>

      <div className="flex flex-col">
        <div className="p-6">
          <Link to="/login" className="inline-flex items-center gap-2 text-sm text-[#5C6B61] hover:text-[#1E4631]" data-testid="back-login">
            <ArrowLeft className="w-4 h-4"/> Retour à la connexion
          </Link>
        </div>

        <div className="flex-1 grid place-items-center px-6 pb-12">
          <div className="w-full max-w-md space-y-6">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#D96C4A]/10 text-[#D96C4A] text-xs font-semibold mb-3">
                <KeyRound className="w-3.5 h-3.5"/> Réinitialisation
              </div>
              <h1 className="text-3xl font-[Outfit] font-bold">Mot de passe oublié</h1>
              <p className="text-[#5C6B61] mt-2 text-sm">
                {step === 1 && "Entrez votre email pour recevoir un code de réinitialisation."}
                {step === 2 && "Saisissez le code reçu et votre nouveau mot de passe."}
                {step === 3 && "Votre mot de passe a été mis à jour."}
              </p>
            </div>

            {step === 1 && (
              <form onSubmit={requestCode} className="space-y-5" data-testid="forgot-step1">
                <div className="space-y-2">
                  <Label htmlFor="fp-email">Email</Label>
                  <Input id="fp-email" type="email" required value={email} onChange={(e)=>setEmail(e.target.value)}
                         placeholder="vous@exemple.cd" data-testid="forgot-email"/>
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-[#1E4631] hover:bg-[#163424] text-white py-6 rounded-lg font-semibold" data-testid="forgot-submit-1">
                  {loading ? "..." : "Envoyer le code"}
                </Button>
                <div className="text-xs text-[#5C6B61] text-center">
                  Un email avec le code sera envoyé à votre adresse si elle est reconnue.
                </div>
              </form>
            )}

            {step === 2 && (
              <form onSubmit={submitReset} className="space-y-5" data-testid="forgot-step2">
                <div className="p-4 rounded-xl bg-[#E3A72F]/10 border border-[#E3A72F]/30">
                  <div className="flex items-center gap-2 text-xs font-bold uppercase text-[#1A1D1A] mb-1">
                    <MailCheck className="w-4 h-4 text-[#E3A72F]"/> Vérifiez votre boîte email
                  </div>
                  <div className="text-sm text-[#1A1D1A]">
                    Nous vous avons envoyé un code à 6 caractères. Il est valide 30 minutes.
                    Vérifiez aussi votre dossier <b>Spam</b> si vous ne le voyez pas.
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="fp-code">Code de réinitialisation</Label>
                  <Input id="fp-code" required value={code} onChange={(e)=>setCode(e.target.value.toUpperCase())}
                         maxLength={6} placeholder="ABCDEF"
                         className="font-mono tracking-[0.3em] text-center text-lg uppercase"
                         data-testid="forgot-code"/>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="fp-new">Nouveau mot de passe</Label>
                  <div className="relative">
                    <Input id="fp-new" type={showPw ? "text" : "password"} required value={newPw}
                           onChange={(e)=>setNewPw(e.target.value)} minLength={6}
                           className="pr-10" data-testid="forgot-newpw"/>
                    <button type="button" onClick={()=>setShowPw(v=>!v)}
                            aria-label={showPw ? "Masquer" : "Afficher"}
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded text-[#5C6B61] hover:text-[#1E4631] hover:bg-[#1E4631]/5 transition"
                            data-testid="forgot-toggle-pw">
                      {showPw ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="fp-confirm">Confirmer le mot de passe</Label>
                  <Input id="fp-confirm" type={showPw ? "text" : "password"} required value={confirmPw}
                         onChange={(e)=>setConfirmPw(e.target.value)} minLength={6}
                         data-testid="forgot-confirm"/>
                </div>

                <Button type="submit" disabled={loading} className="w-full bg-[#D96C4A] hover:bg-[#B85A3D] text-white py-6 rounded-lg font-semibold" data-testid="forgot-submit-2">
                  {loading ? "..." : "Réinitialiser le mot de passe"}
                </Button>

                <button type="button" onClick={()=>{setStep(1); setCode(""); setNewPw(""); setConfirmPw("");}}
                        className="w-full text-xs text-[#5C6B61] hover:text-[#1E4631] underline"
                        data-testid="forgot-back-1">
                  ← Demander un nouveau code
                </button>
              </form>
            )}

            {step === 3 && (
              <div className="space-y-5 text-center bg-white rounded-2xl border border-[#E5EBE7] p-8" data-testid="forgot-step3">
                <div className="w-16 h-16 rounded-full bg-[#2E7D32]/10 grid place-items-center mx-auto">
                  <CheckCircle2 className="w-8 h-8 text-[#2E7D32]"/>
                </div>
                <h2 className="text-2xl font-[Outfit] font-bold">Mot de passe mis à jour</h2>
                <p className="text-sm text-[#5C6B61]">Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.</p>
                <Button onClick={()=>navigate("/login")} className="w-full bg-[#1E4631] hover:bg-[#163424] text-white py-6 rounded-lg font-semibold" data-testid="forgot-goto-login">
                  Aller à la connexion
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
